// Iter102 — Admin > Integrations > Square (Step 25).
// Read-only diagnostic surface. Never displays the access token.
// Uses the backend endpoints already shipped in Iter102A/B.
import React, { useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, Copy } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminFeatureShell from "@/components/admin/AdminFeatureShell";

const Row = ({ k, v, tid, mono }) => (
  <div className="flex justify-between py-1.5 border-b border-white/5 text-xs"
        data-testid={tid}>
    <span className="text-lifted-dim">{k}</span>
    <span className={mono ? "font-mono text-white" : "text-white"}>{v ?? "—"}</span>
  </div>
);

const Dot = ({ ok }) =>
  ok ? <CheckCircle2 className="h-4 w-4 text-lifted-green" />
     : <XCircle className="h-4 w-4 text-rose-400" />;

export default function AdminSquare() {
  const [status, setStatus] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [s, a] = await Promise.all([
        api.get("/admin/integrations/square/status"),
        api.get("/admin/integrations/square/catalog-audit"),
      ]);
      setStatus(s.data); setAudit(a.data);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cfg  = status?.config  || {};
  const live = status?.live    || {};
  const loc  = (live.locations || []).find(L => L.id === cfg.location_id);

  return (
    <AdminFeatureShell
      breadcrumb="LATM / Integrations"
      title="Square"
      description="Primary payment processor. Read-only diagnostic view."
      testId="admin-square"
    >
      <div className="flex justify-end mb-3">
        <button onClick={load} className="btn-lifted-outline text-xs inline-flex items-center gap-1"
                 data-testid="square-refresh">
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {err && <div className="glass-panel border border-rose-500/30 p-3 text-rose-200 text-sm mb-3">{err}</div>}

      {loading && !status ? (
        <div className="text-lifted-dim text-sm">Loading Square status…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Connection card */}
          <div className="glass-panel border border-white/10 rounded-lg p-4"
                data-testid="square-connection-card">
            <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-2 flex items-center gap-2">
              Connection
              <Dot ok={live.ok} />
            </div>
            <Row k="Access token"      v={cfg.access_token_set ? "Configured (server-side only)" : "MISSING"}          tid="sq-row-token" />
            <Row k="Application ID"    v={cfg.application_id_set ? "Configured" : "MISSING"}                            tid="sq-row-appid" />
            <Row k="Environment"       v={cfg.environment || "—"}                                                       tid="sq-row-env" />
            <Row k="Location"          v={loc ? `${loc.name} (${loc.status})` : cfg.location_id || "—"}                 tid="sq-row-loc" mono />
            <Row k="Location ID"       v={cfg.location_id || "—"}                                                       tid="sq-row-loc-id" mono />
            <Row k="Currency"          v={loc?.currency || "USD"}                                                       tid="sq-row-cur" />
            <Row k="SDK installed"     v={cfg.sdk_installed ? "Yes" : "No"}                                             tid="sq-row-sdk" />
            <Row k="Checkout gate"     v={cfg.checkout_enabled ? "ENABLED — LIVE CHARGES" : "DISABLED (safe)"}          tid="sq-row-gate" />
            <Row k="Webhook signature" v={cfg.webhook_signature_key_set ? "Configured — live processing ON" : "Not set — live webhooks OFF"}   tid="sq-row-webhook" />
          </div>

          {/* Catalog audit card */}
          <div className="glass-panel border border-white/10 rounded-lg p-4"
                data-testid="square-catalog-card">
            <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-2">
              Catalog & Mapping
            </div>
            <Row k="Active LATM products"        v={audit?.latm_active_products}                    tid="sq-audit-total" />
            <Row k="Already synced to Square"    v={audit?.latm_products_with_square_id}            tid="sq-audit-synced" />
            <Row k="Still needs sync"            v={audit?.latm_products_needs_sync}                tid="sq-audit-pending" />
            <Row k="Orders with Square payment"  v={audit?.latm_orders_with_square_payment_id}      tid="sq-audit-orders" />
            {audit?.sample_mapping && (
              <div className="mt-3 pt-2 border-t border-white/10 text-[0.65rem]">
                <div className="text-lifted-dim mb-1 uppercase tracking-[0.18em] font-bebas">Sample mapping</div>
                <div className="font-mono text-white truncate">{audit.sample_mapping.title}</div>
                <div className="text-lifted-dim font-mono">square_catalog_id: {audit.sample_mapping.square_catalog_id}</div>
              </div>
            )}
            {audit?.square_error && (
              <div className="mt-2 text-[0.65rem] text-rose-300 flex items-start gap-1">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {audit.square_error}
              </div>
            )}
          </div>

          {/* Live locations */}
          <div className="glass-panel border border-white/10 rounded-lg p-4 lg:col-span-2"
                data-testid="square-locations-card">
            <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-2">
              Merchant Locations ({(live.locations || []).length})
            </div>
            <table className="w-full text-xs">
              <thead className="text-[0.6rem] tracking-[0.18em] font-bebas text-lifted-dim uppercase">
                <tr className="border-b border-white/10">
                  <th className="py-2 text-left">Location ID</th>
                  <th className="py-2 text-left">Name</th>
                  <th className="py-2 text-left">Status</th>
                  <th className="py-2 text-left">Type</th>
                  <th className="py-2 text-left">Currency</th>
                </tr>
              </thead>
              <tbody>
                {(live.locations || []).map(L => (
                  <tr key={L.id} className={`border-b border-white/5 ${L.id === cfg.location_id ? "bg-lifted-green/5" : ""}`}>
                    <td className="py-1.5 font-mono text-white">{L.id}
                      {L.id === cfg.location_id && <span className="text-lifted-green text-[0.6rem] ml-2">ACTIVE</span>}
                    </td>
                    <td className="py-1.5">{L.name}</td>
                    <td className="py-1.5">{L.status}</td>
                    <td className="py-1.5">{L.type || "—"}</td>
                    <td className="py-1.5">{L.currency || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Recent events */}
          <div className="glass-panel border border-white/10 rounded-lg p-4 lg:col-span-2"
                data-testid="square-events-card">
            <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-2">
              Recent Square Events ({(status?.recent_events || []).length})
            </div>
            {(status?.recent_events || []).length === 0 ? (
              <div className="text-lifted-dim text-xs italic">No events yet.</div>
            ) : (
              <ul className="space-y-1 text-xs">
                {status.recent_events.map((e, i) => (
                  <li key={i} className="flex justify-between border-b border-white/5 py-1">
                    <span className="text-white">{e.kind}</span>
                    <span className="text-lifted-dim font-mono">{e.event_type || e.order_id || e.event_id || ""}</span>
                    <span className="text-lifted-dim text-[0.6rem]">{String(e.ts || "").slice(0, 19)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </AdminFeatureShell>
  );
}
